/*eslint no-shadow:0*/
import EventEmitter from 'events';
import { modalHelperFactory } from '../../simple-flow/modal-helper';
// We include this, because we consider a three syncer to be a selector
import ThreeSelectorFactory from '../../helpers/three-selector';

import { EMD_BUTTON_BACK_VENUE_DESIGNER, EMD_SAVING_MAP } from './constants';

const mapContainerFactory = ({ manager }) => {
    const {
        aSyncCart,
        clearErrorMessage,
        clearSuccessMessage,
        get,
        getCartId,
        getChairRadiusInPixels,
        getCheckoutModalVisibility,
        getCheckoutWidgetVisibility,
        getErrorMessage,
        getEventData,
        getEventId,
        getGASectionVariants,
        getHeldSeatIsReleasing,
        getHoverSectionInfo,
        getIsAnimating,
        getIsLoading,
        getLegendDialogVisibility,
        getManageHoldsModalVisibility,
        getNotifications,
        getOrderInfo,
        getOrderPriceInfo,
        getPaymentInstruction,
        getPDF,
        getPrintDialogVisibility,
        getPrintOptionValue,
        getSeatmapViewBox,
        getSectionHoldsInfo,
        getSectionTiersData,
        getSectionTotalUnitsInfo,
        getSoldSeatInfo,
        getSoldSeatInfoFetched,
        getSuccessMessage,
        getSyncCartStatus,
        getTicketSelection,
        getTiers,
        getTotalUnitsInfo,
        getViewMode,
        getWorldToPixelsFactor,
        getZoomButtons,
        gotoSection,
        handleSelectGATickets,
        hasItemsCarted,
        hasSelectedGATickets,
        hideLegendDialog,
        hidePrintDialog,
        idsBelongToGASection,
        off: managerOff,
        on: managerOn,
        pickSeats,
        releaseHold,
        removeCartItem,
        resetSelectedGATickets,
        savePaymentInstruction,
        saveSectionHolds,
        seatmapGetViewport,
        seatmapMouseDown,
        seatmapMouseMove,
        seatmapMouseUp,
        seatmapSetViewport,
        seatmapWheel,
        setAddonQuantity,
        setOrderSummaryTicketPrice,
        setCheckoutModalVisibility,
        setCheckoutWidgetVisibility,
        setManageHoldsModalVisibility,
        setPaymentInstruction,
        setPrintOptionValue,
        setSeatmapValue,
        setSectionHoldQuantity,
        setSelectedGASectionVariantQuantity,
        showManageHoldsModal,
        toggleLegendDialogVisibility,
        togglePrintDialogVisibility,
        setErrorMessage,
    } = manager;

    const _eventEmitter = new EventEmitter();
    const _threeSelector = new ThreeSelectorFactory();

    const _modalHelper = modalHelperFactory({
        path: 'ui',
        setValue: setSeatmapValue,
    });

    const _onChange = () => {
        _eventEmitter.emit('change');
    };

    const getScene = ({ camera }) => {
        const node = get();
        const worldToPixelsFactor = getWorldToPixelsFactor();
        const scene = _threeSelector.getScene({
            camera,
            node,
            worldToPixelsFactor,
        });

        return scene;
    };

    const displaySeatInfoMessage = () => {
        const node = get();
        const eventId = node.globals?.order?.event_id;
        const cart = node.globals?.cart || {};

        const localStorageKey = `eb-seat-info-message-dismissed-${eventId}`;
        const hasDismissedMessage = Boolean(
            localStorage.getItem(localStorageKey),
        );

        return !!Object.keys(cart).length && !hasDismissedMessage;
    };

    const closeInfoMessage = () => {
        const node = get();
        const eventId = node.globals.order.event_id;
        const localStorageKey = `eb-seat-info-message-dismissed-${eventId}`;
        localStorage.setItem(localStorageKey, 'true');
    };

    const init = () => {
        managerOn('change', _onChange);
        // Initial paint so that at least the maps are rendered empty while we wait for data to come

        _onChange();
    };

    const close = () => {
        // Important: avoid memory leak!
        managerOff('change', _onChange);
        // We remove all of our listeners so that we
        // avoid further memory leaks. This may not be
        // necessary but it is healthy to follow this pattern
        _eventEmitter.removeAllListeners();
    };

    const on = (event, handler) => _eventEmitter.on(event, handler);

    const off = (event, handler) =>
        _eventEmitter.removeListener(event, handler);

    const getInteractiveMsg = () => {
        const interactiveMsg = get('ui.interactiveMsg') || '';

        return interactiveMsg;
    };

    const aSaveModal = async () => {
        const actionsCarryOut = {
            [EMD_BUTTON_BACK_VENUE_DESIGNER]: () => null,
        };

        const modalResult =
            (await _modalHelper.aShowModal({ type: EMD_SAVING_MAP })) || {};
        const modalResponse = modalResult.button;

        if (actionsCarryOut[modalResponse]) {
            actionsCarryOut[modalResponse]();
        }
    };

    const saveMap = () => {
        aSaveModal();
    };

    const getCurrentModal = () => _modalHelper.getCurrentModal();

    const handleSelectTicket = ({ ids, variantId }) => {
        const selectedSeats = ids.map((id) => ({ id, variantId }));

        pickSeats({
            selectedSeats,
            value: true,
        });
    };

    const handleCancelTicketSelection = () => {
        manager.aShowTicketSelection({ visible: false });
    };

    const syncCart = () => {
        aSyncCart();
    };

    const onMouseDown = (args) => {
        hideLegendDialog();
        hidePrintDialog();
        seatmapMouseDown(args);
    };

    return {
        displaySeatInfoMessage,
        closeInfoMessage,
        clearErrorMessage,
        clearSuccessMessage,
        close,
        getCartId,
        getChairRadiusInPixels,
        getCheckoutModalVisibility,
        getCheckoutWidgetVisibility,
        getCurrentModal,
        getErrorMessage,
        getEventData,
        getEventId,
        getGASectionVariants,
        getHeldSeatIsReleasing,
        getHoverSectionInfo,
        getInteractiveMsg,
        getIsLoading,
        getLegendDialogVisibility,
        getManageHoldsModalVisibility,
        getNotifications,
        getOrderInfo,
        getOrderPriceInfo,
        getPaymentInstruction,
        getPDF,
        getPrintDialogVisibility,
        getPrintOptionValue,
        getSectionHoldsInfo,
        getSectionTiersData,
        getSectionTotalUnitsInfo,
        getSoldSeatInfo,
        getSoldSeatInfoFetched,
        getSuccessMessage,
        getSyncCartStatus,
        getTicketSelection,
        getTiers,
        getTotalUnitsInfo,
        getViewMode,
        getZoomButtons,
        handleCancelTicketSelection,
        handleGotoSection: gotoSection,
        handleSelectGATickets,
        handleSelectTicket,
        hasItemsCarted,
        hasSelectedGATickets,
        idsBelongToGASection,
        init,
        off,
        on,
        releaseHold,
        removeCartItem,
        resetSelectedGATickets,
        saveMap,
        savePaymentInstruction,
        saveSectionHolds,
        sceneInterface: {
            getScene,
            getViewBox: getSeatmapViewBox,
            getViewport: seatmapGetViewport,
            getIsAnimating,
            seatmapMouseDown: onMouseDown,
            seatmapMouseMove,
            seatmapMouseUp,
            seatmapWheel,
            setViewport: seatmapSetViewport,
        },
        setAddonQuantity,
        setOrderSummaryTicketPrice,
        setCheckoutModalVisibility,
        setCheckoutWidgetVisibility,
        setManageHoldsModalVisibility,
        setPaymentInstruction,
        setPrintOptionValue,
        setSectionHoldQuantity,
        setSelectedGASectionVariantQuantity,
        showManageHoldsModal,
        syncCart,
        toggleLegendDialogVisibility,
        togglePrintDialogVisibility,
    };
};

export default mapContainerFactory;
